import { format as formatPrice } from '@monofront/business/utils/price';
import { formatPrismicData } from '@monofront/business/utils';
import { IPrismicImage } from '@monofront/types';
import React, { useEffect, useState } from 'react';
import { prismicService } from '@services/businessContainer';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { useMediaQuery } from '@monofront/business/hooks';
import useSubscriptionPrice from '@hooks/useSubscriptionPrice';

const DISCOUNTED_PRICE = 0;

interface IMembershipProduct {
  image?: IPrismicImage;
  title?: string;
  freeTrialPeriodMessage?: string;
  indicativeOffer?: string;
}

const MembershipProductVersionA: React.FC = () => {
  const tCart = useTranslations('Cart');
  const { locale: routerLocale } = useRouter();
  const { annualSubscriptionPrice } = useSubscriptionPrice();

  const isMobile = useMediaQuery(800);

  const [data, setData] = useState<IMembershipProduct>({
    title: tCart('MembershipProduct.title'),
    freeTrialPeriodMessage: tCart('MembershipProduct.freeTrialPeriodMessage'),
    indicativeOffer: tCart('MembershipProduct.indicativeOffer'),
  });

  useEffect(() => {
    const getData = async () => {
      const document = await prismicService.getDocumentByUid(
        'membership_product',
        'membership_product',
        undefined,
        routerLocale ? { lang: `${routerLocale}-${routerLocale}` } : {},
      );

      if (document) {
        setData(formatPrismicData(document.data));
      }
    };

    getData();
  }, [routerLocale]);

  const { image, title, freeTrialPeriodMessage, indicativeOffer } = data;

  return (
    <>
      <div className="container" data-testid="MaaP-SubscriptionProduct">
        {image?.url && <img src={image?.url} alt={image?.alt || ''} />}
        <div className="block-text">
          <p className="title">
            <strong>{title}</strong>
          </p>
          <p>
            <span className="price initial-price">
              {tCart('MembershipProduct.price', {
                price: formatPrice(annualSubscriptionPrice, {
                  minimumFractionDigits: 2,
                }),
              })}
            </span>
            <span className="free-trial-message">{freeTrialPeriodMessage}</span>
          </p>
          <p className="indicative-offer">{indicativeOffer}</p>
        </div>
        <p className="price discounted-price">
          {formatPrice(DISCOUNTED_PRICE, { minimumFractionDigits: 2 })}
        </p>
      </div>
      <style jsx>{`
        .container {
          background: var(--clr-primary-3);
          border-radius: var(--rd-3);
          padding: ${isMobile
            ? 'var(--space-4) var(--space-2)'
            : 'var(--space-4)'};
          display: flex;
          gap: var(--space-3);
          justify-content: space-between;
          align-items: center;
        }

        .block-text {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: var(--space-2);

          flex: 1;
        }

        .title {
          font-weight: bold;
          font-size: var(--font-size-2);
        }

        img {
          width: 48px;
          height: 48px;
        }

        .price {
          line-height: 23px;
          font-family: var(--font-family-1);
          font-size: var(--font-size-3);
        }

        .initial-price {
          text-decoration: line-through;
          margin-right: var(--space-2);
        }

        .free-trial-message {
          font-weight: var(--font-weight-light);
          font-size: var(--font-size-2);
        }

        .indicative-offer {
          font-weight: var(--font-weight-light);
          font-size: var(--font-size-1);
          line-height: 12px;
        }

        @media (max-width: 800px) {
          .discounted-price {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default MembershipProductVersionA;
